// =====================================================================
//  ADMIN CONSOLE, ACCESS CONTROL
// ---------------------------------------------------------------------
//  Who may get in, and who may do what once they are in.
//
//  Three things live here because they are the same job:
//    · Access requests, people asking to join the organization.
//    · Removal requests, the platform owner asking to delete one of your
//      users. Your admin has to approve it; that approval is destructive
//      and irreversible, so it is confirmed explicitly.
//    · Roles, the current distribution of privilege, so an administrator
//      can see at a glance how many people hold admin rights.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCheck,
  Mail,
  ShieldAlert,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  Grid,
  KpiTile,
  LoadingBlock,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  PanelHead,
  PersonCell,
  RoleChip,
  SearchInput,
  StatusChip,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import { roleTone } from '../theme';
import { fmtDate, fmtDateTime, fmtNumber, matchesSearch, timeAgo } from '../format';

const ROLE_ORDER = ['admin', 'hr', 'manager', 'employee'];

const ROLE_DESCRIPTION = {
  admin: 'Full control: manage people, payroll, access and settings.',
  hr: 'Manage people, leave, payroll and documents. Cannot change org settings.',
  manager: 'Read the whole organization; manage their team’s records.',
  employee: 'Their own profile, attendance, leave and payslips only.',
};

export default function AccessControlPage({ role, currentUser }) {
  const queryClient = useQueryClient();
  const canDecide = ['admin', 'hr'].includes(role);

  const [tab, setTab] = useState('requests');
  const [search, setSearch] = useState('');
  const [confirmRemoval, setConfirmRemoval] = useState(null);

  const debouncedSearch = useDebounced(search, 250);

  /* ------------------------------- data ------------------------------- */
  const { data: accessRequests = [], isLoading: requestsLoading } = useQuery({
    queryKey: ['admin', 'access-requests'],
    queryFn: () => backendApi.auth.listAccessRequests(),
  });

  const { data: deletionRequests = [], isLoading: deletionsLoading } = useQuery({
    queryKey: ['admin', 'deletion-requests'],
    queryFn: () => backendApi.deletionRequests.list(),
  });

  const { data: peopleData } = useQuery({
    queryKey: ['admin', 'people-roles'],
    queryFn: () => backendApi.entities.Employee.page({ limit: 1000, sort: 'full_name', order: 'asc' }),
  });

  // Pinned identity so the role-count and privileged-user memos are stable.
  const people = useMemo(() => peopleData?.rows || [], [peopleData]);

  /* ---------------------------- decisions ----------------------------- */
  const decideAccess = useMutation({
    mutationFn: ({ id, approve }) =>
      approve ? backendApi.auth.approveAccessRequest(id) : backendApi.auth.rejectAccessRequest(id),
    onSuccess: (_res, { approve }) => {
      toast.success(approve ? 'Access approved, an invite has been emailed' : 'Request rejected');
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not record that decision'),
  });

  const decideRemoval = useMutation({
    mutationFn: ({ id, approve }) =>
      approve ? backendApi.deletionRequests.approve(id) : backendApi.deletionRequests.reject(id),
    onSuccess: (_res, { approve }) => {
      toast.success(approve ? 'User removed' : 'Removal request rejected');
      setConfirmRemoval(null);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not record that decision'),
  });

  /* --------------------------- derived views -------------------------- */
  const pendingRequests = useMemo(
    () => accessRequests.filter((r) => String(r.status || 'pending').toLowerCase() === 'pending'),
    [accessRequests]
  );

  const roleCounts = useMemo(() => {
    const counts = Object.fromEntries(ROLE_ORDER.map((r) => [r, 0]));
    people.forEach((p) => {
      const key = String(p.role || 'employee').toLowerCase();
      if (counts[key] !== undefined) counts[key] += 1;
    });
    return counts;
  }, [people]);

  const privilegedPeople = useMemo(
    () =>
      people
        .filter((p) => ['admin', 'hr', 'manager'].includes(String(p.role).toLowerCase()))
        .filter((p) => matchesSearch(p, debouncedSearch, ['full_name', 'email', 'department', 'position', 'role'])),
    [people, debouncedSearch]
  );

  const neverSignedIn = people.filter((p) => !p.is_active);

  /* ------------------------------ columns ----------------------------- */
  const requestColumns = useMemo(
    () => [
      {
        key: 'full_name',
        label: 'Requester',
        width: 240,
        render: (row) => <PersonCell name={row.full_name} email={row.email} sub={row.email} />,
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        render: (row) => <StatusChip value={row.status || 'Pending'} />,
      },
      {
        key: 'created_at',
        label: 'Requested',
        width: 150,
        render: (row) => <span title={fmtDateTime(row.created_at)}>{timeAgo(row.created_at)}</span>,
        csv: (row) => row.created_at,
      },
    ],
    []
  );

  const roleColumns = useMemo(
    () => [
      {
        key: 'full_name',
        label: 'Person',
        width: 250,
        render: (row) => <PersonCell name={row.full_name} email={row.email} sub={row.email} />,
      },
      { key: 'role', label: 'Role', width: 110, render: (row) => <RoleChip role={row.role} /> },
      { key: 'department', label: 'Department', width: 160 },
      { key: 'position', label: 'Position', width: 180 },
      {
        key: 'is_active',
        label: 'Sign-in',
        width: 130,
        render: (row) =>
          row.is_active ? <Chip tone="positive">Enabled</Chip> : <Chip tone="warning">Never signed in</Chip>,
        csv: (row) => (row.is_active ? 'Enabled' : 'Never signed in'),
      },
      { key: 'join_date', label: 'Joined', width: 115, render: (row) => fmtDate(row.join_date) },
    ],
    []
  );

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Access Control"
        sub="Approve who joins, and review who holds elevated privileges."
      />

      <Grid cols={4} style={{ marginBottom: 14 }}>
        <KpiTile
          label="Access requests"
          value={fmtNumber(pendingRequests.length)}
          icon={UserPlus}
          tone={pendingRequests.length > 0 ? 'warn' : 'pos'}
          foot={pendingRequests.length === 0 ? 'None waiting' : 'Awaiting your decision'}
        />
        <KpiTile
          label="Removal requests"
          value={fmtNumber(deletionRequests.length)}
          icon={UserMinus}
          tone={deletionRequests.length > 0 ? 'neg' : 'pos'}
          foot={deletionRequests.length === 0 ? 'None waiting' : 'Raised by the platform owner'}
        />
        <KpiTile
          label="Administrators"
          value={fmtNumber(roleCounts.admin + roleCounts.hr)}
          icon={ShieldCheck}
          foot={`${roleCounts.admin} admin · ${roleCounts.hr} HR`}
        />
        <KpiTile
          label="Never signed in"
          value={fmtNumber(neverSignedIn.length)}
          icon={Mail}
          tone={neverSignedIn.length > 0 ? 'warn' : 'pos'}
          foot="Accounts not yet activated"
        />
      </Grid>

      {deletionRequests.length > 0 ? (
        <div style={{ marginBottom: 14 }}>
          <Notice
            tone="negative"
            icon={ShieldAlert}
            title={`${deletionRequests.length} account removal request${deletionRequests.length === 1 ? '' : 's'} need your decision`}
          >
            The platform owner has asked to remove {deletionRequests.length === 1 ? 'a user' : 'users'} from your
            organization. Approving permanently deletes the account.
          </Notice>
        </div>
      ) : null}

      <Tabs
        items={[
          { key: 'requests', label: 'Access requests', count: pendingRequests.length },
          { key: 'removals', label: 'Removal requests', count: deletionRequests.length },
          { key: 'roles', label: 'Roles & privileges', count: privilegedPeople.length },
        ]}
        value={tab}
        onChange={setTab}
      />

      <div style={{ marginTop: 14 }}>
        {/* -------------------- ACCESS REQUESTS -------------------- */}
        {tab === 'requests' ? (
          <DataTable
            columns={requestColumns}
            rows={accessRequests}
            loading={requestsLoading}
            defaultSort={{ key: 'created_at', dir: 'desc' }}
            emptyIcon={CheckCheck}
            emptyTitle="No access requests"
            emptyText="When someone asks for access from the sign-in page, their request appears here."
            rowActions={
              canDecide
                ? (row) =>
                    String(row.status || 'pending').toLowerCase() === 'pending' ? (
                      <>
                        <Button
                          size="sm"
                          variant="accent"
                          icon={Check}
                          disabled={decideAccess.isPending}
                          onClick={() => decideAccess.mutate({ id: row.id, approve: true })}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={X}
                          disabled={decideAccess.isPending}
                          onClick={() => decideAccess.mutate({ id: row.id, approve: false })}
                          aria-label="Reject"
                        />
                      </>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--ac-faint)' }}>Decided</span>
                    )
                : undefined
            }
            toolbar={
              <Toolbar>
                <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>
                  Approving emails the person an invitation to set their password.
                </span>
                <ToolbarSpacer />
                <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>
                  {fmtNumber(pendingRequests.length)} pending of {fmtNumber(accessRequests.length)}
                </span>
              </Toolbar>
            }
          />
        ) : null}

        {/* -------------------- REMOVAL REQUESTS -------------------- */}
        {tab === 'removals' ? (
          <Panel>
            <PanelHead
              title="Account removal requests"
              sub="Raised by the platform owner, your approval is required before anything is deleted."
            />
            <PanelBody flush>
              {deletionsLoading ? (
                <LoadingBlock label="Loading requests…" />
              ) : deletionRequests.length === 0 ? (
                <EmptyState
                  icon={CheckCheck}
                  title="No removal requests"
                  text="Nothing is pending. If the platform owner asks to remove one of your users, it will appear here for your approval."
                />
              ) : (
                deletionRequests.map((request) => (
                  <div className="ac-queue-item" key={request.id}>
                    <span
                      className="ac-feed-icon"
                      style={{
                        background: 'var(--ac-neg-bg)',
                        borderColor: 'var(--ac-neg-bd)',
                        color: 'var(--ac-neg-fg)',
                      }}
                    >
                      <UserMinus size={13} strokeWidth={2} />
                    </span>

                    <div className="ac-queue-body">
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ac-ink)' }}>
                        {request.target_user_name || `User #${request.target_user_id}`}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 1 }}>
                        {request.target_user_email}
                        {request.requested_by_name ? ` · requested by ${request.requested_by_name}` : ''}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ac-faint)', marginTop: 2 }}>
                        {timeAgo(request.created_at)}
                      </div>
                    </div>

                    {canDecide ? (
                      <div className="ac-queue-actions" style={{ opacity: 1 }}>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={Check}
                          onClick={() => setConfirmRemoval(request)}
                          disabled={decideRemoval.isPending}
                        >
                          Approve removal
                        </Button>
                        <Button
                          size="sm"
                          icon={X}
                          onClick={() => decideRemoval.mutate({ id: request.id, approve: false })}
                          disabled={decideRemoval.isPending}
                        >
                          Keep user
                        </Button>
                      </div>
                    ) : (
                      <Chip tone="warning">Awaiting an admin</Chip>
                    )}
                  </div>
                ))
              )}
            </PanelBody>
          </Panel>
        ) : null}

        {/* ------------------------- ROLES ------------------------- */}
        {tab === 'roles' ? (
          <>
            <Grid cols={4} style={{ marginBottom: 14 }}>
              {ROLE_ORDER.map((roleKey) => (
                <Panel key={roleKey} pad>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <Chip tone={roleTone(roleKey)}>{roleKey === 'hr' ? 'HR' : roleKey}</Chip>
                    <span className="ac-num" style={{ fontSize: 19, fontWeight: 700, color: 'var(--ac-ink)' }}>
                      {roleCounts[roleKey]}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ac-muted)', lineHeight: 1.5 }}>
                    {ROLE_DESCRIPTION[roleKey]}
                  </p>
                </Panel>
              ))}
            </Grid>

            <Notice tone="info" icon={ShieldCheck} title="Roles are changed on the person's record">
              Open <strong>People</strong>, edit the employee, and set their access level. Only an admin can grant the
              admin role.
            </Notice>

            <div style={{ marginTop: 14 }}>
              <DataTable
                columns={roleColumns}
                rows={privilegedPeople}
                defaultSort={{ key: 'role', dir: 'asc' }}
                emptyIcon={Users}
                emptyTitle="No elevated accounts"
                emptyText="Nobody currently holds admin, HR or manager privileges."
                toolbar={
                  <Toolbar>
                    <SearchInput value={search} onChange={setSearch} placeholder="Search privileged users…" width={240} />
                    <ToolbarSpacer />
                    <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>
                      {fmtNumber(privilegedPeople.length)} with elevated access
                    </span>
                  </Toolbar>
                }
              />
            </div>
          </>
        ) : null}
      </div>

      <ConfirmDialog
        open={Boolean(confirmRemoval)}
        onClose={() => setConfirmRemoval(null)}
        onConfirm={() => decideRemoval.mutate({ id: confirmRemoval.id, approve: true })}
        loading={decideRemoval.isPending}
        title="Approve this account removal?"
        confirmLabel="Delete the account"
        message={
          <>
            <strong>{confirmRemoval?.target_user_name || 'This user'}</strong> will be permanently deleted and will lose
            all access immediately. This cannot be undone.
          </>
        }
      />
    </div>
  );
}
